import React, { memo, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Provider, useStore, useDispatch } from 'react-redux';
import { gridReady, updateQueryURL, setActionType, updateReportsFilters } from '../../actions/reports';
import css from './Grid.css';
import TotalsCell from '../widgets/TotalsCell';
import GridService from '../../services/grid';
import {
    autoGroupColumnDef,
    getTotalsRowData,
    createRowIdGetter,
    calculatedMetricSumAggFunc,
    calculatedMetricCountAggFunc,
    calculatedMetricMaxAggFunc,
    calculatedMetricMinAggFunc,
    calculatedMetricAvgAggFunc,
    calculatedMetricFirstAggFunc,
    calculatedMetricLastAggFunc,
    AGG_OPERATORS,
    getColumnWidthsMap,
    USER_COLUMN_RESIZE_SOURCES,
} from '../../utils/grids';
import GridGeneralCellRenderer from '../widgets/GridGeneralCellRenderer';
import GridCustomHeader from '../widgets/GridCustomHeader';
import { CREATIVE_IMAGE } from '../../utils/reportsConsts';
import { isObjectEmpty } from '../../utils/general';
import { useFetchTotals } from '../../reports/totals';
import { extractFromUrl, parseReportQueryFromHash, ReportUrlParams } from '../../utils/url';

const COLUMN_TYPES = {
    METRIC: 'metric',
    DIMENSION: 'dimension',
};

const DEFAULT_COL_DEF = {
    minWidth: 50,
    floatingFilter: true,
};

const AG_GRID_PIVOT_ROW_PREFIX = /^ag-Grid-AutoColumn-/;

const DEFAULT_COLUMN_DEFS = [];
const DEFAULT_ROW_DATA = [];
const DEFAULT_COLUMN_TYPES = {};
const DEFAULT_POST_PROCESS_FILTERS_UI = {};
const DEFAULT_SET_EXPORT_PARAMS = () => {};

const Grid = props => {
    const {
        id = 'generic_grid',
        columnDefs = DEFAULT_COLUMN_DEFS,
        reportType = null,
        pivotTable: pivotTableProp,
        pivotMode: pivotModeProp = false,
        pinnedColumns: pinnedColumnsProp,
        columnOrder: columnOrderProp,
        rowData = DEFAULT_ROW_DATA,
        totalsData: initialTotalsData,
        className = '',
        children = null,
        hidePivotHeaders = false,
        hideColumnsSideBar = false,
        exportParams = null,
        setExportParams = DEFAULT_SET_EXPORT_PARAMS,
        columnTypes = DEFAULT_COLUMN_TYPES,
        onMoveColumn,
        onTableFilterChanged,
        postProcessFiltersUI = DEFAULT_POST_PROCESS_FILTERS_UI,
        disableUrlSync = false,
        queryObject,
        persistColumnWidths: persistColumnWidthsProp,
        columnWidths: columnWidthsProp,
        onColumnWidthsChange,
    } = props;

    const store = useStore();
    const dispatch = useDispatch();

    const onReady = () => dispatch(gridReady(reportType));
    const onUpdateQueryURL = useCallback(
        (pivotState, columnOrder, pinnedColumns, columnWidths, persistColumnWidths) =>
            dispatch(
                setActionType(
                    updateQueryURL(
                        reportType,
                        pivotState,
                        columnOrder,
                        pinnedColumns,
                        columnWidths,
                        persistColumnWidths
                    ),
                    reportType
                )
            ),
        [dispatch, reportType]
    );

    const { totalsData, isLoading, fetchTotals } = useFetchTotals(reportType, id, queryObject);

    const [visible, setVisible] = useState(false);
    const [pivotMode, setPivotMode] = useState(pivotModeProp);

    const gridApiRef = useRef(null);
    const gridServiceRef = useRef(null);

    // Track isLoading in a ref to avoid re-rendering grid column definitions
    const isTotalsLoadingRef = useRef(isLoading);
    isTotalsLoadingRef.current = isLoading;

    // Pass isLoading to createRowIdGetter to handle the totals row re-rendering
    const getRowId = useMemo(() => createRowIdGetter(columnDefs, isLoading), [columnDefs, isLoading]);

    const pinnedTopRowData = useMemo(() => {
        if (!rowData?.length) return [];

        if (gridApiRef.current?.getDisplayedRowCount() === 0) return [];

        const totals = getTotalsRowData(totalsData || initialTotalsData || {});
        return totals.length > 0 ? totals : isLoading ? [{ __isTotalsRow: true }] : [];
    }, [initialTotalsData, totalsData, isLoading, rowData]);

    const normalizeColumnId = useCallback(colId => colId.replace(AG_GRID_PIVOT_ROW_PREFIX, ''), []);

    // Reports read width state from the URL (mirrors column_order); dashboards pass it via props.
    // Parse the URL only when the hash actually changes (not on every render) — reading the hash is cheap.
    const urlHash = window.location.hash;
    const urlWidthState = useMemo(() => {
        if (disableUrlSync) return { persist: undefined, widths: undefined };
        const parsed = parseReportQueryFromHash(urlHash);
        return {
            persist: parsed?.[ReportUrlParams.PERSIST_COLUMN_WIDTHS] ?? null,
            widths: parsed?.[ReportUrlParams.COLUMN_WIDTHS] ?? null,
        };
    }, [urlHash, disableUrlSync]);

    const effectivePersistColumnWidths = disableUrlSync
        ? persistColumnWidthsProp ?? true
        : urlWidthState.persist ?? true;
    // Stabilize the widths object by value so the column-defs memo doesn't churn every render.
    const sourceColumnWidthsJson = JSON.stringify((disableUrlSync ? columnWidthsProp : urlWidthState.widths) || null);
    const sourceColumnWidths = useMemo(() => JSON.parse(sourceColumnWidthsJson), [sourceColumnWidthsJson]);

    const gridColumnDefs = useMemo(() => {
        const urlColumnOrder = extractFromUrl(ReportUrlParams.COLUMN_ORDER);

        const columnOrder =
            (urlColumnOrder?.length && urlColumnOrder) ||
            (columnOrderProp?.length && columnOrderProp) ||
            columnDefs.map(({ colId }) => colId);

        const orderedColumns = [
            ...new Set(
                columnOrder
                    .map(colId => columnDefs.find(col => normalizeColumnId(col.colId) === normalizeColumnId(colId)))
                    .filter(Boolean)
            ),
        ];

        // Ensure new fields not in column order are still shown
        const normalizedColumnOrder = new Set(columnOrder.map(normalizeColumnId));
        const { newDimensions, newNonDimensions } = columnDefs.reduce(
            (acc, col) => {
                if (normalizedColumnOrder.has(normalizeColumnId(col.colId))) {
                    return acc;
                }

                if (col.type === COLUMN_TYPES.DIMENSION) {
                    acc.newDimensions.push(col);
                } else {
                    acc.newNonDimensions.push(col);
                }

                return acc;
            },
            { newDimensions: [], newNonDimensions: [] }
        );

        let allColumns;
        if (newDimensions.length || newNonDimensions.length) {
            const firstNonDimensionIndex = orderedColumns.findIndex(col => col.type !== COLUMN_TYPES.DIMENSION);
            if (firstNonDimensionIndex >= 0) {
                allColumns = [
                    ...orderedColumns.slice(0, firstNonDimensionIndex),
                    ...newDimensions,
                    ...orderedColumns.slice(firstNonDimensionIndex),
                    ...newNonDimensions,
                ];
            } else {
                allColumns = [...orderedColumns, ...newDimensions, ...newNonDimensions];
            }
        } else {
            allColumns = orderedColumns;
        }

        return allColumns.map(cell => {
            return {
                ...cell,
                cellRenderer: cellProps => {
                    const isPinnedTop = cellProps.node.rowPinned === 'top';
                    const CellComponent = isPinnedTop ? TotalsCell : GridGeneralCellRenderer;

                    return (
                        <Provider store={store}>
                            <CellComponent
                                {...cellProps}
                                reportType={reportType}
                                isLoading={isPinnedTop && isTotalsLoadingRef.current}
                            />
                        </Provider>
                    );
                },
            };
        });
    }, [columnDefs, columnOrderProp, store, reportType, normalizeColumnId]);

    const filterValidColumns = useCallback(
        fields => fields?.filter(field => columnDefs.some(cd => cd.field === field)) || [],
        [columnDefs]
    );

    const isPivotWithColumns = useCallback(api => pivotMode && api?.getPivotColumns()?.length, [pivotMode]);

    const updateTotals = useCallback(
        filterModel => {
            // Totals calculation in the BE for pivot mode with columns is not supported.
            if (isPivotWithColumns(gridApiRef.current)) return;

            fetchTotals(columnDefs, filterModel || {});
        },
        [fetchTotals, isPivotWithColumns, columnDefs]
    );

    const applyPivotTableSettings = useCallback(() => {
        if (!gridApiRef.current || !pivotMode) return;

        const defaultSettings = {
            rowGroup: columnDefs.filter(colDef => colDef.type === COLUMN_TYPES.DIMENSION).map(col => col.field),
            values: columnDefs.filter(colDef => colDef.type === COLUMN_TYPES.METRIC).map(col => col.field),
            columns: [],
        };

        const urlPivotTable = extractFromUrl(ReportUrlParams.PIVOT_TABLE);

        const pivotTable =
            (!isObjectEmpty(urlPivotTable) && urlPivotTable) ||
            (!isObjectEmpty(pivotTableProp) && pivotTableProp) ||
            {};

        const settings = { ...defaultSettings, ...pivotTable };

        gridApiRef.current.setRowGroupColumns(filterValidColumns(settings.rowGroup));
        gridApiRef.current.setPivotColumns(filterValidColumns(settings.columns));
        gridApiRef.current.setValueColumns(filterValidColumns(settings.values));
    }, [pivotMode, pivotTableProp, filterValidColumns, columnDefs]);

    const applyPinnedColumns = useCallback(() => {
        const api = gridApiRef.current;
        if (!api) return;

        const urlPinnedColumns = extractFromUrl(ReportUrlParams.PINNED_COLUMNS);
        const pinned =
            (!isObjectEmpty(urlPinnedColumns) && urlPinnedColumns) ||
            (!isObjectEmpty(pinnedColumnsProp) && pinnedColumnsProp) ||
            {};

        if (!pinned) return;

        const { left = [], right = [] } = pinned;

        const columns = api.getAllDisplayedColumns?.() || [];
        const findColId = colId => columns.find(col => col.getColId() === colId)?.getColId();

        const state = [
            ...left.map(colId => ({ colId: findColId(colId), pinned: 'left' })),
            ...right.map(colId => ({ colId: findColId(colId), pinned: 'right' })),
        ].filter(({ colId }) => colId);

        if (!state.length) return;

        api.applyColumnState({ state, defaultState: { pinned: null } });
    }, [pinnedColumnsProp]);

    const applyFiltersFromURL = useCallback(() => {
        if (isObjectEmpty(postProcessFiltersUI)) return;

        const filterModel = Object.fromEntries(Object.entries(postProcessFiltersUI));
        gridApiRef.current.setFilterModel(filterModel);
    }, [postProcessFiltersUI]);

    // Saved widths are restored imperatively (see applySavedColumnWidths). Here we only auto-size the
    // columns that DON'T have a saved width, so auto-size never clobbers a width we're about to restore.
    const autoSizeColumnsRespectingSavedWidths = useCallback(() => {
        const api = gridApiRef.current;
        if (!api || api.isDestroyed()) return;

        const savedWidths = effectivePersistColumnWidths ? sourceColumnWidths : null;
        const savedColIds = new Set(savedWidths ? Object.keys(savedWidths) : []);

        const columnsToAutoSize = (api.getAllDisplayedColumns?.() || [])
            .filter(col => !savedColIds.has(col.getColId()))
            .map(col => col.getColId());

        if (columnsToAutoSize.length) {
            api.autoSizeColumns(columnsToAutoSize);
        }
    }, [effectivePersistColumnWidths, sourceColumnWidths]);

    // Restore saved widths IMPERATIVELY (not via colDef) so columnDefs don't change on resize — a
    // colDef.width/initialWidth that depends on saved widths makes ag-Grid reconcile columns on every
    // resize and reset the user's drag. Keyed by actual colId, so it covers table, pivot value columns
    // (generated at runtime), and the auto-group row column. Re-applying the same widths is a no-op,
    // and setColumnWidths fires onColumnResized with source 'api' (which capture ignores) — no loop.
    const applySavedColumnWidths = useCallback(() => {
        const api = gridApiRef.current;
        if (!api || api.isDestroyed() || !effectivePersistColumnWidths) return;

        // Read the CURRENT source of truth at call time. For reports the widths live in the URL hash, and
        // URL updates (updateQueryURL) don't trigger a re-render — so the memoized sourceColumnWidths can be
        // stale and would re-apply an old map over a finished resize on a later column event. Dashboards pass
        // widths via props, which are fresh because widget updates do re-render.
        const widths = disableUrlSync ? sourceColumnWidths : extractFromUrl(ReportUrlParams.COLUMN_WIDTHS);
        if (!widths) return;

        const updates = Object.entries(widths)
            .filter(([, width]) => typeof width === 'number' && width > 0)
            .map(([key, width]) => ({ key, newWidth: Math.max(width, DEFAULT_COL_DEF.minWidth) }));

        if (updates.length) {
            api.setColumnWidths(updates);
        }
    }, [effectivePersistColumnWidths, disableUrlSync, sourceColumnWidths]);

    const onGridReady = useCallback(
        params => {
            gridApiRef.current = params.api;
            gridServiceRef.current = new GridService(id, params.api);

            applyPivotTableSettings();
            applyPinnedColumns();
            applyFiltersFromURL();
            autoSizeColumnsRespectingSavedWidths();
            applySavedColumnWidths();

            setTimeout(() => setVisible(true), 0);

            onReady();
        },
        [
            id,
            applyPivotTableSettings,
            applyFiltersFromURL,
            applyPinnedColumns,
            autoSizeColumnsRespectingSavedWidths,
            applySavedColumnWidths,
        ]
    );

    const updateURLQuery = useCallback(
        ({ api }) => {
            if (disableUrlSync) return;

            const pivotState =
                !api || api.isDestroyed() || !pivotMode
                    ? null
                    : {
                          rowGroup: api.getRowGroupColumns().map(col => col.getColId()),
                          columns: api.getPivotColumns().map(col => col.getColId()),
                          values: api.getValueColumns().map(col => col.getColId()),
                      };

            const columns = api.getAllDisplayedColumns?.() || [];

            const getPinnedColumns = direction =>
                columns.filter(col => col.getPinned?.() === direction).map(col => col.getColId());

            const left = getPinnedColumns('left');
            const right = getPinnedColumns('right');

            const pinnedColumns = left.length || right.length ? { left, right } : null;

            // Can't save pivot order with columns because it must be stored in the URL in a flattened format
            const columnOrder = !isPivotWithColumns(api)
                ? api.getAllDisplayedColumns().map(col => normalizeColumnId(col.getColId()))
                : null;

            // Widths use flat per-column ids, so they can be captured in pivot too. But pivot-with-columns
            // generates many long, data-derived colIds; serializing all of them into the report URL on every
            // drag risks URL bloat. So in pivot-with-columns we don't capture NEW widths into the URL, but we
            // must PRESERVE any already-saved widths — serializeReportQuery rebuilds the whole query and would
            // otherwise drop column_widths when passed null/empty, wiping them even while persist is true.
            // (Dashboards store widths in the widget config via onColumnWidthsChange, not the URL — unaffected.)
            let columnWidths = null;
            if (effectivePersistColumnWidths) {
                columnWidths = isPivotWithColumns(api)
                    ? extractFromUrl(ReportUrlParams.COLUMN_WIDTHS)
                    : getColumnWidthsMap(api);
            }

            onUpdateQueryURL(pivotState, columnOrder, pinnedColumns, columnWidths, effectivePersistColumnWidths);
        },
        [
            pivotMode,
            onUpdateQueryURL,
            normalizeColumnId,
            disableUrlSync,
            isPivotWithColumns,
            effectivePersistColumnWidths,
        ]
    );

    const onColumnMoved = useCallback(
        e => {
            const { finished, api, column, toIndex } = e;
            if (!finished || !api) return;

            if (pivotMode && column) {
                const normalizedMovedColId = normalizeColumnId(column.getColId());
                const rowGroupColumns = api.getRowGroupColumns();
                const oldIndex = rowGroupColumns.findIndex(col => col.getColId() === normalizedMovedColId);

                if (oldIndex !== -1) {
                    const newRowGroupIndex = Math.min(toIndex, rowGroupColumns.length);

                    if (oldIndex !== newRowGroupIndex) {
                        api.moveRowGroupColumn(oldIndex, newRowGroupIndex);
                    }
                }
            }

            if (!isPivotWithColumns(api)) {
                const newOrder = api.getAllDisplayedColumns().map(col => normalizeColumnId(col.getColId()));
                onMoveColumn?.({ columnOrder: newOrder });
            }

            updateURLQuery(e);
        },
        [onMoveColumn, updateURLQuery, normalizeColumnId, isPivotWithColumns, pivotMode]
    );

    const onColumnResized = useCallback(
        e => {
            // Only persist a deliberate, finished user drag (allowlist — never auto-size/fit/api/flex).
            if (!e.finished || !e.api || !USER_COLUMN_RESIZE_SOURCES.includes(e.source)) return;
            if (!effectivePersistColumnWidths) return;

            if (disableUrlSync) {
                onColumnWidthsChange?.(getColumnWidthsMap(e.api));
            } else {
                updateURLQuery(e);
            }
        },
        [effectivePersistColumnWidths, disableUrlSync, onColumnWidthsChange, updateURLQuery]
    );

    const onFilterChanged = useCallback(() => {
        const filterModel = gridApiRef.current.getFilterModel();
        updateTotals(filterModel);
        onTableFilterChanged?.(filterModel);
        dispatch(updateReportsFilters(reportType, filterModel, columnDefs));
    }, [updateTotals, onTableFilterChanged, dispatch, reportType, columnDefs]);

    const closeToolPanel = useCallback(() => gridApiRef.current.openToolPanel(null), []);

    const onColumnPivotModeChanged = useCallback(() => {
        closeToolPanel();
        setPivotMode(!pivotMode);
    }, [pivotMode, closeToolPanel]);

    useEffect(() => {
        if (!exportParams) return;

        gridApiRef.current.exportDataAsCsv(exportParams);
        setExportParams(null);
    }, [exportParams, setExportParams]);

    useEffect(() => (pivotMode ? applyPivotTableSettings() : gridApiRef.current?.setRowGroupColumns([])), [
        pivotMode,
        applyPivotTableSettings,
    ]);

    const prevPersistRef = useRef(effectivePersistColumnWidths);
    useEffect(() => {
        const wasOff = prevPersistRef.current === false;
        prevPersistRef.current = effectivePersistColumnWidths;
        if (!wasOff || !effectivePersistColumnWidths || !gridApiRef.current || gridApiRef.current.isDestroyed()) return;
        if (isPivotWithColumns(gridApiRef.current)) return;
        if (disableUrlSync) {
            onColumnWidthsChange?.(getColumnWidthsMap(gridApiRef.current));
        } else {
            updateURLQuery({ api: gridApiRef.current });
        }
    }, [
        effectivePersistColumnWidths,
        disableUrlSync,
        onColumnWidthsChange,
        normalizeColumnId,
        isPivotWithColumns,
        updateURLQuery,
    ]);

    const sideBar = useMemo(() => {
        const toolPanels = [];

        if (!hideColumnsSideBar) {
            const columnsPanel = {
                id: 'columns',
                labelDefault: 'Columns',
                labelKey: 'columns',
                iconKey: 'columns',
                toolPanel: 'agColumnsToolPanel',
                toolPanelParams: {
                    suppressRowGroups: !pivotMode,
                    suppressPivots: !pivotMode,
                    suppressValues: !pivotMode,
                    suppressSideButtons: false,
                    suppressColumnSelectAll: false,
                    suppressColumnExpandAll: false,
                },
            };

            toolPanels.push(columnsPanel);
        }

        toolPanels.push({
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel',
            toolPanelParams: {
                suppressExpandAll: false,
                suppressFilterSearch: false,
                suppressSyncLayoutWithGrid: false,
            },
        });

        return { toolPanels };
    }, [hideColumnsSideBar, pivotMode]);

    const includeAssets = useMemo(() => gridColumnDefs?.find(({ field }) => field === CREATIVE_IMAGE), [
        gridColumnDefs,
    ]);

    if (!rowData?.length) return null;

    const pivotDisplayMode = pivotMode && !hidePivotHeaders ? 'always' : 'never';

    // Register custom aggregation functions for calculated metrics using standard AgGrid names
    // This overrides the built-in functions to handle calculated metric objects correctly
    const aggFuncs = useMemo(
        () => ({
            [AGG_OPERATORS.SUM]: calculatedMetricSumAggFunc,
            [AGG_OPERATORS.COUNT]: calculatedMetricCountAggFunc,
            [AGG_OPERATORS.MAX]: calculatedMetricMaxAggFunc,
            [AGG_OPERATORS.MIN]: calculatedMetricMinAggFunc,
            [AGG_OPERATORS.AVG]: calculatedMetricAvgAggFunc,
            [AGG_OPERATORS.FIRST]: calculatedMetricFirstAggFunc,
            [AGG_OPERATORS.LAST]: calculatedMetricLastAggFunc,
        }),
        []
    );

    return (
        <div className={classNames(className, css.gridWithChart, { [css.visible]: visible })}>
            {children}
            <div className={classNames(css.gridContainer)}>
                <AgGridReact
                    className="ag-theme-quartz"
                    modules={[AllEnterpriseModule]}
                    rowData={rowData}
                    getRowId={getRowId}
                    columnDefs={gridColumnDefs}
                    autoGroupColumnDef={autoGroupColumnDef}
                    defaultColDef={DEFAULT_COL_DEF}
                    suppressDragLeaveHidesColumns
                    suppressAggFuncInHeader
                    groupHideOpenParents
                    groupDefaultExpanded={-1}
                    pivotDefaultExpanded={-1}
                    suppressContextMenu
                    allowDragFromColumnsToolPanel
                    sideBar={sideBar}
                    columnTypes={columnTypes}
                    pinnedTopRowData={pinnedTopRowData}
                    pivotMode={pivotMode}
                    rowGroupPanelShow={pivotDisplayMode}
                    pivotPanelShow={pivotDisplayMode}
                    components={{ GridCustomHeader }}
                    aggFuncs={aggFuncs}
                    onGridReady={onGridReady}
                    rowHeight={includeAssets ? 48 : 40}
                    onColumnMoved={onColumnMoved}
                    onColumnResized={onColumnResized}
                    onColumnEverythingChanged={applySavedColumnWidths}
                    onFilterChanged={onFilterChanged}
                    onRowClicked={closeToolPanel}
                    onColumnPivotModeChanged={onColumnPivotModeChanged}
                    onColumnRowGroupChanged={updateURLQuery}
                    onColumnPivotChanged={updateURLQuery}
                    onColumnValueChanged={updateURLQuery}
                    onColumnPinned={updateURLQuery}
                    onColumnVisible={updateURLQuery}
                    animateRows={false}
                    rowBuffer={10}
                />
            </div>
        </div>
    );
};

Grid.propTypes = {
    columnDefs: PropTypes.arrayOf(PropTypes.object),
    rowData: PropTypes.arrayOf(PropTypes.object),
    totalsData: PropTypes.arrayOf(PropTypes.object),
    columnTypes: PropTypes.objectOf(PropTypes.any),
    id: PropTypes.string,
    children: PropTypes.element,
    reportType: PropTypes.string,
    pivotMode: PropTypes.bool,
    pivotTable: PropTypes.objectOf(PropTypes.any),
    pinnedColumns: PropTypes.shape({
        left: PropTypes.arrayOf(PropTypes.string),
        right: PropTypes.arrayOf(PropTypes.string),
    }),
    columnOrder: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    hidePivotHeaders: PropTypes.bool,
    hideColumnsSideBar: PropTypes.bool,
    exportParams: PropTypes.objectOf(PropTypes.any),
    setExportParams: PropTypes.func,
    onMoveColumn: PropTypes.func,
    onTableFilterChanged: PropTypes.func,
    postProcessFiltersUI: PropTypes.objectOf(PropTypes.any),
    disableUrlSync: PropTypes.bool,
    queryObject: PropTypes.objectOf(PropTypes.any),
    persistColumnWidths: PropTypes.bool,
    columnWidths: PropTypes.objectOf(PropTypes.number),
    onColumnWidthsChange: PropTypes.func,
};

export default memo(Grid);
